import { Facebook, Instagram, Users, RefreshCw, Target, Sparkles } from "lucide-react";
import AdsPlatformPage from "../components/site/AdsPlatformPage";
import { usePageSEO, breadcrumb, serviceSchema, faqSchema } from "../hooks/usePageSEO";

const config = {
  meta: { eyebrow: "Meta Ads · Facebook + Instagram" },
  hero: {
    title: "Crea demanda y",
    highlight: "escala adquisición",
  },
  intro: {
    eyebrow: "Demanda + Remarketing",
    title: "Meta no es para gastar — es para construir un activo de audiencia.",
    body:
      "Facebook + Instagram bien orquestados crean demanda donde Google solo la recolecta. Mi enfoque es construir audiencias, creatividades y secuencias que escalan sin colapsar tu CAC.",
  },
  services: [
    { icon: Facebook, title: "Facebook Ads", desc: "Lead Ads, conversiones, mensajes y catálogos con configuración avanzada." },
    { icon: Instagram, title: "Instagram Ads", desc: "Reels, stories, feed y DM Ads enchufados a IA conversacional." },
    { icon: Users, title: "Audiencias avanzadas", desc: "Lookalikes, custom audiences, exclusiones y segmentación por comportamiento real." },
    { icon: RefreshCw, title: "Remarketing inteligente", desc: "Secuencias que recuperan visitantes, abandonos de carrito y conversaciones frías." },
    { icon: Target, title: "Conversion API", desc: "Tracking server-side, deduplicación de eventos y match quality alto." },
    { icon: Sparkles, title: "Iteración creativa", desc: "Pipeline semanal de hooks, formatos y ángulos para no quemar audiencias." },
  ],
  approach: [
    "Conversion API + Pixel funcionando con match quality > 7 antes de escalar.",
    "Estructura de campañas pensada para aprendizaje, no para rendir el primer día.",
    "Creatividades probadas en cohortes pequeñas antes de meter presupuesto serio.",
    "Lead Ads conectados a WhatsApp y CRM en tiempo real, no por export manual.",
    "Remarketing por etapas: visitantes, conversaciones, no compradores y clientes.",
    "Reportes con CAC, ROAS, frecuencia, fatiga creativa y micro-conversions.",
  ],
  whyItems: [
    { title: "Operador real", desc: "Yo edito audiencias, copy, presupuestos y creatividades. No es solo estrategia." },
    { title: "Sistema, no campañas sueltas", desc: "Cada lead aterriza en un flujo automatizado con IA y handoff humano." },
    { title: "Pruebas reales por dato", desc: "Hipótesis claras, métricas claras y decisiones rápidas — sin slides infinitos." },
  ],
  faqs: [
    { q: "¿Lead Ads o tráfico a sitio web?", a: "Depende del producto. En servicios y B2B suelo combinar ambos: Lead Ads para captura rápida y conversiones a landing para audiencias frías." },
    { q: "¿Cuánto presupuesto necesito?", a: "Recomiendo desde $1,000 USD/mes en cuentas iniciales. Para escalar con confianza el rango sano arranca en $3,000+." },
    { q: "¿Manejas cuentas de Business Manager existentes?", a: "Sí. Hago auditoría inicial, ordeno la estructura y conecto Conversion API si no está bien implementada." },
  ],
  related: [
    { to: "/google-ads", label: "Google Ads", desc: "Combina Meta para crear demanda con Google para capturarla." },
    { to: "/automatizacion-ia", label: "Automatización con IA", desc: "Cada lead conectado a flujos automatizados de respuesta inmediata." },
    { to: "/funnels", label: "Funnels", desc: "Tráfico → automatización → calificación → ventas." },
  ],
  resultStat: {
    value: "5x",
    label: "más conversión cuando respondes en menos de 5 minutos",
    footnote: "Estudio Lead Connect / HubSpot.",
  },
};

export default function MetaAdsPage() {
  usePageSEO({
    title: "Facebook & Instagram Ads | Daos Ads",
    description:
      "Meta Ads (Facebook + Instagram) gestionados con criterio: audiencias avanzadas, Conversion API, creatividades iteradas y conexión directa con IA conversacional para escalar adquisición sin quemar presupuesto.",
    path: "/meta-ads",
    jsonLd: [
      breadcrumb([
        { name: "Inicio", path: "/" },
        { name: "Meta Ads", path: "/meta-ads" },
      ]),
      serviceSchema({
        name: "Meta Ads · Facebook e Instagram Ads",
        description:
          "Operación experta de Facebook Ads e Instagram Ads con audiencias avanzadas, remarketing por etapas, Conversion API server-side y pipeline creativo.",
        path: "/meta-ads",
      }),
      faqSchema(config.faqs),
    ],
  });
  return <AdsPlatformPage {...config} />;
}
